/**
 * Dispatches a single-field CPT-table edit to the matching per-field reducer.
 *
 * The actual reducer implementations live in the {@link module:updateRowData}
 * folder, grouped by concern (dates, visits, PA status, J-code, description,
 * authorization number).
 * @module updateRowDataForDifferentKeys
 */
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { CptCodeWithDetailsKeys } from "../../../../../enums/tableColumnKeys";
import { updateRowDataForAuthorizationNumber } from "./updateRowData/authorizationNumberReducer";
import {
  updateRowDataForEndAt,
  updateRowDataForStartFrom,
} from "./updateRowData/dateReducers";
import { updateRowDataForCptCodesEditableDescription } from "./updateRowData/descriptionReducer";
import { updateRowDataForFilingInfo } from "./updateRowData/filingInfoReducer";
import { updateRowDataForJCodes } from "./updateRowData/jCodeReducer";
import { updateRowDataForPaRequired } from "./updateRowData/paRequiredReducer";
import { updateRowDataForPendingNumber } from "./updateRowData/pendingNumberReducer";
import {
  updateRowDataForRemainingVisits,
  updateRowDataForVisits,
  updateRowDataForVisitsAllowed,
} from "./updateRowData/visitReducers";

/**
 * Applies a single-field edit to one CPT row, including validation side effects where applicable.
 *
 * @param key - {@link CptCodeWithDetailsKeys} column being edited
 * @param rowDataToUpdate - Existing row snapshot
 * @param updatedData - Incoming cell value from the table control
 * @param generalOrderDetails - Order context (date of service, regimen, etc.)
 * @param authOnFileDetails - Auth-on-file context for document column refresh
 * @param category - Healthcare facility `category` driving date validation exemptions
 * @returns New row object; returns the input row when `key` is not handled
 */
export const updateRowDataForDifferentKeys = (
  key: string,
  rowDataToUpdate: CptCodesTableRowModel,
  updatedData: any,
  generalOrderDetails: GeneralOrderDetails | null | undefined,
  authOnFileDetails: AuthOnFile | null | undefined,
  category?: string | null,
) => {
  switch (key) {
    case CptCodeWithDetailsKeys.StartFrom:
      return updateRowDataForStartFrom(
        rowDataToUpdate,
        updatedData,
        generalOrderDetails,
        category,
      );

    case CptCodeWithDetailsKeys.EndAt:
      return updateRowDataForEndAt(
        rowDataToUpdate,
        updatedData,
        generalOrderDetails,
      );
    case CptCodeWithDetailsKeys.RemainingVisits:
      return updateRowDataForRemainingVisits(rowDataToUpdate, updatedData);
    case CptCodeWithDetailsKeys.VisitsAllowed:
      return updateRowDataForVisitsAllowed(rowDataToUpdate, updatedData);
    case CptCodeWithDetailsKeys.Visits:
      return updateRowDataForVisits(rowDataToUpdate, updatedData);
    case CptCodeWithDetailsKeys.PaRequired:
      return updateRowDataForPaRequired(
        rowDataToUpdate,
        updatedData,
        generalOrderDetails,
        authOnFileDetails,
        category,
      );
    case CptCodeWithDetailsKeys.JCodes:
      return updateRowDataForJCodes(rowDataToUpdate, updatedData);
    case CptCodeWithDetailsKeys.CptCodesEditableDescription:
      return updateRowDataForCptCodesEditableDescription(
        rowDataToUpdate,
        updatedData,
      );
    case CptCodeWithDetailsKeys.AuthorizationNumber:
      return updateRowDataForAuthorizationNumber(rowDataToUpdate, updatedData);
    case CptCodeWithDetailsKeys.FilingInfo:
      return updateRowDataForFilingInfo(rowDataToUpdate, updatedData);
    case CptCodeWithDetailsKeys.PendingNumber:
      return updateRowDataForPendingNumber(rowDataToUpdate, updatedData);
    default:
      return rowDataToUpdate;
  }
};
